import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { auth } from '../../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Custom hook for inbox data fetching and management
 * Supports both Outlook and Gmail
 */
export const useInboxData = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [outlookStatus, setOutlookStatus] = useState<any>(null);
  const [googleStatus, setGoogleStatus] = useState<any>(null);
  const [emails, setEmails] = useState<any[]>([]);
  const [threads, setThreads] = useState<any[]>([]);
  const [cases, setCases] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  
  const userHasSelectedRef = useRef(false);
  
  // Fetch all inbox data (including Google)
  const fetchData = useCallback(async () => {
    try {
      const token = auth.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const [emailsRes, casesRes, clientsRes, outlookStatusRes, googleStatusRes] = await Promise.all([
        axios.get(`${API}/inbox/messages?threaded=true&limit=100`, { headers }),
        axios.get(`${API}/cases`, { headers }),
        axios.get(`${API}/clients`, { headers }),
        axios.get(`${API}/outlook/status`, { headers }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/google/calendar/status`, { headers }).catch(() => ({ data: { connected: false } }))
      ]);
      
      const emailData = emailsRes.data;
      let allEmails = emailData.messages || [];
      let allThreads = emailData.threads || [];
      
      // If Google is connected, also fetch Gmail messages
      if (googleStatusRes.data?.connected) {
        try {
          const gmailRes = await axios.get(`${API}/google/mail/messages?max_results=50`, { headers });
          const gmailMessages = gmailRes.data?.messages || [];
          
          // Convert Gmail messages to threads format (each email is its own thread for now)
          // Ensure 'from' property matches expected structure for EmailListPanel
          const gmailThreads = gmailMessages.map((e: any) => {
            const fromData = e.from || { name: 'Ukjent', email: '' };
            const latestMessage = {
              id: e.id,
              subject: e.subject || '(Ingen emne)',
              from: fromData,
              to: e.to || [],
              sender: fromData.email || fromData.name || 'Ukjent',
              senderName: fromData.name || fromData.email || 'Ukjent',
              receivedDateTime: e.date,
              created_at: e.date,
              body: e.snippet,
              body_preview: e.snippet,
              is_read: e.is_read,
              hasAttachments: e.has_attachments,
              has_attachments: e.has_attachments,
              source: 'google',
              labels: e.labels || []
            };
            
            return {
              id: e.thread_id || e.id,
              conversation_id: e.thread_id || e.id,
              subject: e.subject || '(Ingen emne)',
              from: fromData,
              sender: fromData.email || fromData.name || 'Ukjent',
              senderName: fromData.name || fromData.email || 'Ukjent',
              receivedDateTime: e.date,
              created_at: e.date,
              body: e.snippet,
              is_read: e.is_read,
              isRead: e.is_read,
              hasAttachments: e.has_attachments,
              has_attachments: e.has_attachments,
              source: 'google',
              message_count: 1,
              latest_message: latestMessage,
              messages: [latestMessage]
            };
          });
          
          // Add source to outlook threads
          const outlookThreads = allThreads.map((t: any) => ({ ...t, source: 'outlook' }));
          
          // Combine and sort by date
          allThreads = [...outlookThreads, ...gmailThreads].sort((a, b) => {
            const dateA = new Date(a.receivedDateTime || a.date || 0);
            const dateB = new Date(b.receivedDateTime || b.date || 0);
            return dateB.getTime() - dateA.getTime();
          });
          
          // Also update emails for backwards compatibility
          const outlookEmails = allEmails.map((e: any) => ({ ...e, source: 'outlook' }));
          const googleEmails = gmailMessages.map((e: any) => ({ 
            ...e, 
            source: 'google',
            id: e.id,
            subject: e.subject,
            sender: e.from?.email || e.from?.name,
            senderName: e.from?.name || e.from?.email,
            receivedDateTime: e.date,
            body: e.snippet,
            isRead: e.is_read,
            hasAttachments: e.has_attachments
          }));
          
          allEmails = [...outlookEmails, ...googleEmails].sort((a, b) => {
            const dateA = new Date(a.receivedDateTime || a.date || 0);
            const dateB = new Date(b.receivedDateTime || b.date || 0);
            return dateB.getTime() - dateA.getTime();
          });
        } catch (gmailErr) {
          console.error('Error fetching Gmail:', gmailErr);
        }
      }
      
      setEmails(allEmails);
      setThreads(allThreads);
      setCases(casesRes.data?.cases || casesRes.data || []);
      setClients(clientsRes.data || []);
      setOutlookStatus(outlookStatusRes.data);
      setGoogleStatus(googleStatusRes.data);
    } catch (err) {
      console.error('Error fetching inbox data:', err);
      toast.error('Kunne ikke laste innboks');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Refresh emails quietly (no loading state)
  const refreshEmailsQuietly = useCallback(async () => {
    try {
      const token = auth.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(`${API}/inbox/messages?threaded=true&limit=100`, { headers });
      
      const data = response.data;
      let allEmails = data.messages || [];
      let allThreads = data.threads || [];
      
      // Check if Google is connected and fetch Gmail too
      if (googleStatus?.connected) {
        try {
          const gmailRes = await axios.get(`${API}/google/mail/messages?max_results=50`, { headers });
          const gmailMessages = gmailRes.data?.messages || [];
          
          const outlookEmails = allEmails.map((e: any) => ({ ...e, source: 'outlook' }));
          const googleEmails = gmailMessages.map((e: any) => {
            const fromData = e.from || { name: 'Ukjent', email: '' };
            return { 
              ...e, 
              source: 'google',
              id: e.id,
              subject: e.subject,
              from: fromData,
              sender: fromData.email || fromData.name,
              senderName: fromData.name || fromData.email,
              receivedDateTime: e.date,
              created_at: e.date,
              body: e.snippet,
              body_preview: e.snippet,
              isRead: e.is_read,
              is_read: e.is_read,
              hasAttachments: e.has_attachments,
              has_attachments: e.has_attachments
            };
          });
          
          // Also create Gmail threads
          const gmailThreads = gmailMessages.map((e: any) => {
            const fromData = e.from || { name: 'Ukjent', email: '' };
            const latestMessage = {
              id: e.id,
              subject: e.subject || '(Ingen emne)',
              from: fromData,
              to: e.to || [],
              sender: fromData.email || fromData.name || 'Ukjent',
              senderName: fromData.name || fromData.email || 'Ukjent',
              receivedDateTime: e.date,
              created_at: e.date,
              body: e.snippet,
              body_preview: e.snippet,
              is_read: e.is_read,
              hasAttachments: e.has_attachments,
              has_attachments: e.has_attachments,
              source: 'google',
              labels: e.labels || []
            };
            
            return {
              id: e.thread_id || e.id,
              conversation_id: e.thread_id || e.id,
              subject: e.subject || '(Ingen emne)',
              from: fromData,
              sender: fromData.email || fromData.name || 'Ukjent',
              senderName: fromData.name || fromData.email || 'Ukjent',
              receivedDateTime: e.date,
              created_at: e.date,
              body: e.snippet,
              is_read: e.is_read,
              isRead: e.is_read,
              hasAttachments: e.has_attachments,
              has_attachments: e.has_attachments,
              source: 'google',
              message_count: 1,
              latest_message: latestMessage,
              messages: [latestMessage]
            };
          });
          
          // Combine and sort
          const outlookThreads = allThreads.map((t: any) => ({ ...t, source: 'outlook' }));
          allThreads = [...outlookThreads, ...gmailThreads].sort((a, b) => {
            const dateA = new Date(a.receivedDateTime || a.created_at || 0);
            const dateB = new Date(b.receivedDateTime || b.created_at || 0);
            return dateB.getTime() - dateA.getTime();
          });
          
          allEmails = [...outlookEmails, ...googleEmails].sort((a, b) => {
            const dateA = new Date(a.receivedDateTime || a.date || 0);
            const dateB = new Date(b.receivedDateTime || b.date || 0);
            return dateB.getTime() - dateA.getTime();
          });
        } catch (gmailErr) {
          console.error('Error refreshing Gmail:', gmailErr);
        }
      }
      
      setEmails(allEmails);
      setThreads(allThreads);
    } catch (err) {
      console.error('Error refreshing emails:', err);
    }
  }, [googleStatus]);
  
  // Sync from Outlook
  const syncFromOutlook = useCallback(async (showToast = true) => {
    setSyncing(true);
    try {
      const token = auth.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      // Sync Outlook
      const response = await axios.post(
        `${API}/outlook/sync?limit=50`,
        {},
        { headers }
      );
      
      const syncedCount = response.data?.synced_count || response.data?.synced || 0;
      
      // Also sync Gmail if connected
      let gmailCount = 0;
      if (googleStatus?.connected) {
        try {
          const gmailRes = await axios.get(`${API}/google/mail/messages?max_results=20`, { headers });
          gmailCount = gmailRes.data?.count || 0;
        } catch (gmailErr) {
          console.error('Gmail sync error:', gmailErr);
        }
      }
      
      if (showToast) {
        if (syncedCount > 0 || gmailCount > 0) {
          const parts = [];
          if (syncedCount > 0) parts.push(`${syncedCount} fra Outlook`);
          if (gmailCount > 0) parts.push(`${gmailCount} fra Gmail`);
          toast.success(`Synkroniserte ${parts.join(' og ')}`);
        } else {
          toast.info('Ingen nye e-poster');
        }
      }
      
      await refreshEmailsQuietly();
    } catch (err) {
      console.error('Sync error:', err);
      if (showToast) {
        toast.error('Kunne ikke synkronisere');
      }
    } finally {
      setSyncing(false);
    }
  }, [refreshEmailsQuietly, googleStatus]);
  
  // Sync quietly (for background polling)
  const syncFromOutlookQuietly = useCallback(async () => {
    try {
      const token = auth.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(
        `${API}/outlook/sync?limit=25&fast_mode=true`,
        {},
        { headers }
      );
      await refreshEmailsQuietly();
    } catch (err) {
      // Silent fail for background sync
    }
  }, [refreshEmailsQuietly]);
  
  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Setup polling interval
  useEffect(() => {
    const pollInterval = setInterval(() => refreshEmailsQuietly(), 30000);
    const syncInterval = setInterval(() => syncFromOutlookQuietly(), 180000);
    
    return () => {
      clearInterval(pollInterval);
      clearInterval(syncInterval);
    };
  }, [refreshEmailsQuietly, syncFromOutlookQuietly]);
  
  return {
    loading,
    syncing,
    outlookStatus,
    googleStatus,
    emails,
    threads,
    cases,
    clients,
    setEmails,
    setThreads,
    fetchData,
    refreshEmailsQuietly,
    syncFromOutlook,
    syncFromOutlookQuietly,
    userHasSelectedRef
  };
};

export default useInboxData;
